import { useTranslation } from 'react-i18next';
import { InfiosAlert, InfiosIcon } from '@infios/uikit/react';
import type { OrderInsightPayload } from '../../interfaces/chat';
import styles from './DataSourceWarning.module.css';

interface DataSourceWarningProps {
  dataSources: OrderInsightPayload['dataSources'];
}

/**
 * Inline degraded-data banner. Per NFR 3.3 this must never fail silently: any
 * `UNAVAILABLE` source gets a visible warning. `oms` should never be
 * `UNAVAILABLE` on a payload the backend actually sends (the contract says the
 * whole `order_insight` event should be withheld in that case) — but we still
 * render defensively rather than trust that invariant blindly.
 */
export function DataSourceWarning({ dataSources }: DataSourceWarningProps) {
  const { t } = useTranslation();

  const unavailable = (['oms', 'wms', 'tms'] as const).filter((key) => dataSources[key] === 'UNAVAILABLE');
  if (unavailable.length === 0) return null;

  return (
    <div className={styles.wrap} data-testid="data-source-warning">
      {unavailable.map((key) => (
        <InfiosAlert key={key} variant={key === 'oms' ? 'danger' : 'warning'} open className={styles.banner}>
          <InfiosIcon slot="icon" name={key === 'oms' ? 'alert-circle' : 'alert'} />
          {t(`ChatbotUI.OrderInsight.DataSourceUnavailable_${key}`)}
        </InfiosAlert>
      ))}
    </div>
  );
}
